import React, { useState, useEffect, useContext } from 'react';
import { WalletContext } from '../context/WalletContext';

const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// Module-level registry — collects EIP-6963 announcements from page load so
// wallets that only announce once (e.g. Rabby) are still available when the
// picker opens later.
const _eip6963Registry = new Map();
window.addEventListener('eip6963:announceProvider', (e) => {
  const { info, provider } = e.detail || {};
  if (info?.rdns && provider) _eip6963Registry.set(info.rdns, { info, provider });
});
window.dispatchEvent(new CustomEvent('eip6963:requestProvider'));
const dappUrl = encodeURIComponent(window.location.href);
const dappHost = window.location.host + window.location.pathname;

// Fallback install links shown when no EIP-6963 wallets are detected (desktop)
const INSTALL_WALLETS = [
  {
    id: 'metamask', name: 'MetaMask', color: '#e8831a',
    icon: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 318.6 318.6"><path fill="%23e2761b" d="M274.1 35.5l-99.5 73.9L193 65.8z"/><path fill="%23e4761b" d="M44.4 35.5l98.7 74.6-17.5-44.3zm193.9 171.3l-26.5 40.6 56.7 15.6 16.3-55.3zm-204.4.9l16.2 55.3 56.7-15.6-26.5-40.6z"/><path fill="%23e4761b" d="M103.6 138.2l-15.8 23.9 56.3 2.5-2-60.5zm111.3 0l-39-34.8-2.6 61 56.2-2.5zm-111.3 109.2l33.8-16.5-29.2-22.8zm78.1-16.5l33.9 16.5-4.7-39.3z"/><path fill="%23d7c1b3" d="M215 247.4l-33.9-16.5 2.7 22.1-.3 9.3zm-111.4 0l31.5 14.9-.2-9.3 2.6-22.1z"/><path fill="%23233447" d="M135.8 190.5l-28.2-8.3 19.9-9.1zm46.9 0l8.3-17.4 20 9.1z"/><path fill="%23cd6116" d="M103.6 247.4l4.8-40.6-31.3.9zm106.6-40.6l4.8 40.6 26.5-39.7zm23.9-65.6l-56.2 2.5 5.2 28.9 8.3-17.4 20 9.1zm-108.9 23.1l20-9.1 8.2 17.4 5.3-28.9-56.3-2.5z"/><path fill="%23e4751f" d="M87.8 162.1l23.6 46-.8-22.9zm120.3 23.1l-1 22.9 23.7-46zm-64-20.6l-5.3 28.9 6.6 34.1 1.5-44.9zm30.5 0l-2.7 18 1.2 45 6.7-34.1z"/><path fill="%23f6851b" d="M183.8 190.5l-6.7 34.1 4.8 3.3 29.2-22.8 1-22.9zm-75.8-8.3l.8 22.9 29.2 22.8 4.8-3.3-6.6-34.1z"/><path fill="%23c0ad9e" d="M184.3 262.3l.3-9.3-2.5-2.2h-37.7l-2.4 2.2.2 9.3-31.5-14.9 11 9 22.3 15.5h38.3l22.4-15.5 11-9z"/><path fill="%23161616" d="M181.8 230.9l-4.8-3.3h-27.7l-4.8 3.3-2.6 22.1 2.4-2.2h37.7l2.5 2.2z"/><path fill="%23763d16" d="M278.3 114.2l8.5-40.8-12.7-37.9-96.2 71.4 37 31.3 52.3 15.3 11.6-13.5-5-3.6 8-7.3-6.2-4.8 8-6.1zm-237.6-40.8l8.5 40.8-5.4 4 8 6.1-6.1 4.8 8 7.3-5 3.6 11.5 13.5 52.3-15.3 37-31.3-96.1-71.4z"/><path fill="%23f6851b" d="M267.2 153.5l-52.3-15.3 15.9 23.9-23.7 46 31.2-.4h46.5zm-163.6-15.3l-52.3 15.3-17.4 54.2h46.4l31.1.4-23.6-46zm71 26.4l3.3-57.7 15.2-41.1h-67.5l15 41.1 3.5 57.7 1.2 18.2.1 44.8h27.7l.2-44.8z"/></svg>',
    installUrl: 'https://metamask.io/download/',
  },
  {
    id: 'rabby', name: 'Rabby', color: '#8697ff',
    icon: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="8" fill="%238697ff"/><text x="50%25" y="55%25" dominant-baseline="middle" text-anchor="middle" font-size="18" fill="white">R</text></svg>',
    installUrl: 'https://rabby.io/',
  },
];

// Mobile deep link wallets
const MOBILE_WALLETS = [
  {
    id: 'metamask', name: 'MetaMask', color: '#e8831a',
    icon: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 318.6 318.6"><path fill="%23e2761b" d="M274.1 35.5l-99.5 73.9L193 65.8z"/><path fill="%23e4761b" d="M44.4 35.5l98.7 74.6-17.5-44.3zm193.9 171.3l-26.5 40.6 56.7 15.6 16.3-55.3zm-204.4.9l16.2 55.3 56.7-15.6-26.5-40.6z"/><path fill="%23e4761b" d="M103.6 138.2l-15.8 23.9 56.3 2.5-2-60.5zm111.3 0l-39-34.8-2.6 61 56.2-2.5zm-111.3 109.2l33.8-16.5-29.2-22.8zm78.1-16.5l33.9 16.5-4.7-39.3z"/><path fill="%23d7c1b3" d="M215 247.4l-33.9-16.5 2.7 22.1-.3 9.3zm-111.4 0l31.5 14.9-.2-9.3 2.6-22.1z"/><path fill="%23233447" d="M135.8 190.5l-28.2-8.3 19.9-9.1zm46.9 0l8.3-17.4 20 9.1z"/></svg>',
    deepLink: `https://metamask.app.link/dapp/${dappHost}`,
  },
  {
    id: 'coinbase', name: 'Coinbase Wallet', color: '#0052ff',
    icon: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="16" fill="%230052ff"/><rect x="10" y="10" width="12" height="12" rx="3" fill="white"/></svg>',
    deepLink: `https://go.cb-wallet.com/dapp?url=${dappUrl}`,
  },
  {
    id: 'trust', name: 'Trust Wallet', color: '#3375bb',
    icon: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="8" fill="%233375bb"/><path d="M16 6l8 3.5v8c0 4.5-3.5 8-8 10-4.5-2-8-5.5-8-10v-8z" fill="white" fill-opacity="0.9"/></svg>',
    deepLink: `https://link.trustwallet.com/open_url?url=${dappUrl}`,
  },
];

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
    backdropFilter: 'blur(8px)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  },
  modal: {
    background: 'rgba(8,8,8,0.97)',
    border: '1px solid rgba(255,255,255,0.04)',
    borderTop: '1px solid rgba(160,160,160,0.18)',
    borderLeft: '1px solid rgba(160,160,160,0.10)',
    borderRadius: '16px',
    boxShadow: '0 0 50px rgba(160,160,160,0.04), 0 18px 70px rgba(0,0,0,0.8)',
    padding: '24px', width: '320px', maxWidth: '92vw',
    fontFamily: '"Share Tech Mono",monospace',
  },
  title: { color: '#0ef8f8', fontSize: '11px', letterSpacing: '3px', marginBottom: '6px' },
  sub:   { color: 'rgba(255,255,255,0.25)', fontSize: '10px', letterSpacing: '1px', marginBottom: '20px' },
  row: () => ({
    display: 'flex', alignItems: 'center', gap: '12px', width: '100%', marginBottom: '10px',
    padding: '12px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '8px', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: '12px',
    letterSpacing: '1px', transition: 'border-color 0.15s',
    fontFamily: '"Share Tech Mono",monospace', textDecoration: 'none',
  }),
  badge: (c) => ({ marginLeft: 'auto', fontSize: '9px', color: c, flexShrink: 0 }),
  note:  { color: 'rgba(255,255,255,0.2)', fontSize: '9px', letterSpacing: '1px', marginTop: '4px' },
  err:   { color: '#e8003a', fontSize: '9px', letterSpacing: '1px', marginTop: '10px' },
  cancel: {
    marginTop: '14px', width: '100%', background: 'transparent',
    border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.2)', fontSize: '9px',
    letterSpacing: '2px', padding: '8px', borderRadius: '6px',
    cursor: 'pointer', fontFamily: '"Share Tech Mono",monospace',
  },
};

export default function WalletPickerModal({ open, onClose, onConnected }) {
  const { connectWallet } = useContext(WalletContext);
  const [err, setErr] = useState(null);
  // Seed from registry so wallets that only announce on page load (e.g. Rabby) are visible
  const [discovered, setDiscovered] = useState(() => Array.from(_eip6963Registry.values()));
  const [connecting, setConnecting] = useState(null); // rdns of wallet being connected

  // Keep listening for late announcers and refresh when modal opens
  useEffect(() => {
    if (!open || isMobile) return;

    // Sync state with registry (may have grown since component first mounted)
    setDiscovered(Array.from(_eip6963Registry.values()));

    // Fire a fresh request for wallets that respond on demand
    window.dispatchEvent(new CustomEvent('eip6963:requestProvider'));
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setErr(null);
      setConnecting(null);
    }
  }, [open]);

  if (!open) return null;

  const hover   = (e, color) => { e.currentTarget.style.borderColor = color; };
  const unhover = (e)        => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; };

  // EIP-6963 wallets detected → definitely desktop with extensions; skip mobile path
  const showDesktopEip6963 = discovered.length > 0;

  const handleEip6963Connect = async (info, provider) => {
    setErr(null);
    setConnecting(info.rdns);
    localStorage.setItem('xyr_eip6963_rdns', info.rdns); // persist for auto-reconnect on reload
    try {
      await connectWallet(false, provider);
      onClose();
      if (onConnected) onConnected();
    } catch (e) {
      localStorage.removeItem('xyr_eip6963_rdns');
      setErr(e.message || 'Connection failed');
    } finally {
      setConnecting(null);
    }
  };

  const handleFallbackConnect = async () => {
    setErr(null);
    setConnecting('fallback');
    // Mark as injected so startup auto-reconnect knows to use window.ethereum (single wallet, no conflict)
    localStorage.setItem('xyr_eip6963_rdns', '__injected__');
    try {
      await connectWallet();
      onClose();
      if (onConnected) onConnected();
    } catch (e) {
      localStorage.removeItem('xyr_eip6963_rdns');
      setErr(e.message || 'Connection failed');
    } finally {
      setConnecting(null);
    }
  };

  // Mobile: show deep links (only when no EIP-6963 extensions detected)
  if (isMobile && !showDesktopEip6963) {
    return (
      <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) { setErr(null); onClose(); } }}>
        <div style={S.modal}>
          <div style={S.title}>CONNECT WALLET</div>
          <div style={S.sub}>OPEN THIS PAGE IN YOUR WALLET BROWSER</div>
          {MOBILE_WALLETS.map(w => (
            <a key={w.id} href={w.deepLink} style={S.row()}
              onMouseEnter={e => hover(e, w.color)} onMouseLeave={unhover}>
              <img src={w.icon} alt={w.name} width={28} height={28} style={{ borderRadius: 6, flexShrink: 0 }} />
              {w.name}
              <span style={S.badge('#0ef8f8')}>OPEN ↗</span>
            </a>
          ))}
          <div style={S.note}>OPENS THIS PAGE INSIDE THE WALLET'S BROWSER</div>
          {err && <div style={S.err}>{err}</div>}
          <button style={S.cancel} onClick={() => { setErr(null); onClose(); }}>CANCEL</button>
        </div>
      </div>
    );
  }

  // Desktop with EIP-6963 discovered wallets (or mobile browser that somehow has extensions)
  if (showDesktopEip6963) {
    return (
      <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) { setErr(null); onClose(); } }}>
        <div style={S.modal}>
          <div style={S.title}>CONNECT WALLET</div>
          <div style={S.sub}>SELECT YOUR WALLET</div>
          {discovered.map(({ info, provider }) => {
            const isConnecting = connecting === info.rdns;
            return (
              <button key={info.rdns} style={S.row()} disabled={!!connecting}
                onMouseEnter={e => hover(e, '#0ef8f8')} onMouseLeave={unhover}
                onClick={() => handleEip6963Connect(info, provider)}>
                {info.icon
                  ? <img src={info.icon} alt={info.name} width={28} height={28} style={{ borderRadius: 6, flexShrink: 0 }} />
                  : <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
                }
                {info.name}
                <span style={S.badge(isConnecting ? '#f5c200' : 'rgba(255,255,255,0.2)')}>
                  {isConnecting ? 'CONNECTING...' : 'DETECTED'}
                </span>
              </button>
            );
          })}
          {err && <div style={S.err}>{err}</div>}
          <button style={S.cancel} onClick={() => { setErr(null); onClose(); }}>CANCEL</button>
        </div>
      </div>
    );
  }

  // Desktop fallback: window.ethereum present but no EIP-6963 (old wallet), or no wallet at all
  const hasInjected = typeof window.ethereum !== 'undefined';
  return (
    <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) { setErr(null); onClose(); } }}>
      <div style={S.modal}>
        <div style={S.title}>CONNECT WALLET</div>
        <div style={S.sub}>{hasInjected ? 'SELECT YOUR WALLET' : 'INSTALL A WALLET EXTENSION'}</div>
        {INSTALL_WALLETS.map(w => {
          if (hasInjected) {
            const isConnecting = connecting === 'fallback';
            return (
              <button key={w.id} style={S.row()} disabled={!!connecting}
                onMouseEnter={e => hover(e, w.color)} onMouseLeave={unhover}
                onClick={handleFallbackConnect}>
                <img src={w.icon} alt={w.name} width={28} height={28} style={{ borderRadius: 6, flexShrink: 0 }} />
                {w.name}
                <span style={S.badge(isConnecting ? '#f5c200' : 'rgba(255,255,255,0.2)')}>
                  {isConnecting ? 'CONNECTING...' : 'INJECTED'}
                </span>
              </button>
            );
          }
          return (
            <a key={w.id} href={w.installUrl} target="_blank" rel="noreferrer" style={S.row()}
              onMouseEnter={e => hover(e, w.color)} onMouseLeave={unhover}>
              <img src={w.icon} alt={w.name} width={28} height={28} style={{ borderRadius: 6, flexShrink: 0 }} />
              {w.name}
              <span style={S.badge('#f5c200')}>INSTALL ↗</span>
            </a>
          );
        })}
        {!hasInjected && <div style={S.note}>INSTALL AN EXTENSION, THEN RELOAD THIS PAGE</div>}
        {err && <div style={S.err}>{err}</div>}
        <button style={S.cancel} onClick={() => { setErr(null); onClose(); }}>CANCEL</button>
      </div>
    </div>
  );
}
